import {Banner, Button} from '@wandb/components';
// @todo: Migrate to 'DropdownMenu' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import ModifiedDropdown from '@wandb/weave/common/components/elements/ModifiedDropdown';
// @todo: Migrate to 'Input' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import Input from '@wandb/weave/common/components/Input';
import {Option} from '@wandb/weave/common/util/uihelpers';
import React from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {InputOnChangeData} from 'semantic-ui-react';

import {Analytics} from '../services/analytics';
import * as urls from '../util/urls';
import {RoleDropdown} from './MembersTable/AccountSettingsMembersTable/RoleDropdown';
import {TeamMemberRole} from './MembersTable/TeamMembersTable/utils';
import {RoleOption} from './MembersTable/TeamOrOrganizationRoleOption';
import {OrganizationMemberRole} from './MembersTable/util';
import {TeamInviteOptions} from './TeamInviteOptions';

type SingleInviteFormProps = {
  emailOrUsername: string;
  setEmailOrUsername: (value: string) => void;
  teams: string[];
  setTeams: (teams: string[]) => void;
  role: TeamMemberRole | OrganizationMemberRole;
  setRole: (role: TeamMemberRole | OrganizationMemberRole) => void;
  errorMsg: string | null;
  setErrorMsg: (error: string | null) => void;
  showContactSalesOnError: boolean;
  teamOptions: Option[];
  enabledRoleOptions: RoleOption[];
  isTeamInvite: boolean;
  isAddingExistingUser: boolean;
  existingTeamName: string;
  existingOrgMember: string;
  organizationName: string;
  analyticsLocation: string;
  onSubmit: () => void;
};

/**
 * Form component for inviting a single user to an organization or team.
 *
 * @param props - The component props
 * @returns JSX element containing the invite form
 */
export const SingleInviteForm: React.FC<SingleInviteFormProps> = ({
  emailOrUsername,
  setEmailOrUsername,
  teams,
  setTeams,
  role,
  setRole,
  errorMsg,
  setErrorMsg,
  showContactSalesOnError,
  teamOptions,
  enabledRoleOptions,
  isTeamInvite,
  isAddingExistingUser,
  existingTeamName,
  existingOrgMember,
  organizationName,
  analyticsLocation,
  onSubmit,
}) => {
  return (
    <div className="h-full flex flex-col">
      <div className="mb-10 mt-10 flex-1">
        <Banner
          variant="info"
          data-test="create-team-models-info-banner"
          isDismissible={false}>
          <Banner.Body>
            Once the user accepts the invitation, their access to Models can be
            adjusted. By default, they'll receive a full models seat, if
            available.
          </Banner.Body>
        </Banner>
        {errorMsg && (
          <div className="mb-10 mt-10">
            <Banner
              variant="error"
              data-test="create-team-errors"
              isDismissible={false}>
              <Banner.Body>{errorMsg}</Banner.Body>
              {showContactSalesOnError && (
                <Banner.Action
                  href={urls.contactSalesSeats()}
                  target="_blank"
                  onPress={() => {
                    Analytics.track(
                      'Contact sales clicked from invite drawer',
                      {
                        organizationName,
                        location: analyticsLocation,
                        error: errorMsg,
                      }
                    );
                  }}>
                  Contact sales in order to purchase more.
                </Banner.Action>
              )}
            </Banner>
          </div>
        )}

        <div className="mt-10 flex flex-col justify-start">
          <label
            htmlFor="addUserDrawerEmailOrUsername"
            className="mb-1 block font-semibold">
            Email or username
          </label>
          {isAddingExistingUser ? (
            <Input
              id="addUserDrawerEmailOrUsername"
              style={{
                flexGrow: 2,
              }}
              disabled={isAddingExistingUser}
              data-test="add-member-input"
              placeholder={existingOrgMember}
              value={emailOrUsername}
              onChange={(
                e: React.ChangeEvent<HTMLInputElement>,
                {value}: InputOnChangeData
              ) => setEmailOrUsername(value)}
            />
          ) : isTeamInvite ? (
            <TeamInviteOptions
              emailOrUsername={emailOrUsername}
              setEmailOrUsername={value => {
                setEmailOrUsername(value);
                setErrorMsg(null); // Clear error when user types
              }}
              organizationName={organizationName}
              teamName={isTeamInvite ? existingTeamName : undefined}
              placeholder={'Email or username'}
            />
          ) : (
            <Input
              id="addUserDrawerEmailOrUsername"
              style={{
                flexGrow: 2,
              }}
              data-test="add-member-input"
              placeholder="Email or username"
              value={emailOrUsername}
              onChange={(
                e: React.ChangeEvent<HTMLInputElement>,
                {value}: InputOnChangeData
              ) => {
                setEmailOrUsername(value);
                setErrorMsg(null); // Clear error when user types
              }}
            />
          )}
        </div>

        <div className="mt-10 flex flex-col justify-start">
          <label className="mb-1 block font-semibold">
            {isTeamInvite ? 'Team' : 'Choose team(s)'}
          </label>
          <ModifiedDropdown
            className="mr-2"
            style={{flexGrow: 2, minWidth: '150px', boxShadow: 'none'}}
            data-test="add-member-teams-dropdown"
            options={teamOptions}
            value={teams}
            disabled={isTeamInvite}
            placeholder={isTeamInvite ? existingTeamName : 'Select team(s)'}
            fluid
            multiple
            search
            selection
            onChange={(e, {value}) => {
              setTeams(value as string[]);
            }}
          />
        </div>

        <div className="mt-10 flex flex-col justify-start">
          <label className="mb-1 block font-semibold">
            {isTeamInvite || isAddingExistingUser
              ? 'Select team role'
              : 'Select role'}
          </label>
          <RoleDropdown
            role={role}
            roleOptions={enabledRoleOptions}
            setRole={setRole}
          />
        </div>
      </div>
      <div className="flex-shrink-0">
        <Button
          fullWidth
          data-test="add-member-button"
          variant="primary"
          size="md"
          onPress={onSubmit}>
          {isAddingExistingUser ? 'Add user' : 'Send invite'}
        </Button>
      </div>
    </div>
  );
};
